// MODELUL INSTALAȚIILOR DIN DOCUMENTUL SOLUȚIEI `mep`: ce a scris motorul de
// pe server (`mep/motor/iesire.py`, formatul 3.24 §43) adus în forma pe care o
// desenează pagina — `MepSegment` → `Segment`, `MepEquipment` → `Echipament`,
// regulile `INS.*` din `compliance.rules` → `Verificare`.
//
// Geometria trece prin aceleași transformări ca a vizualizatorului (`northUp`,
// apoi originea locală a desenului), deci traseele stau pe pereții planului.
// Cotele vin din `props["mep.segment"].z`, câte una pe vârf: punctele
// formatului sunt plane.

import type { Cheie } from "../i18n";
import { laOrigineLocala, northUp, origineDesen, type CanonicalDoc } from "../viewer/canonical";
import {
  PIESE_SANITARE,
  type Aparat, type Apartament, type Circuit, type Echipament, type FelEchipament, type Fiting, type Intrare,
  type Material, type Mediu, type ModelInstalatii, type PiesaSanitara, type Punct3, type Rol, type Segment,
  type Sistem, type TipCircuit, type Verdict, type Verificare,
} from "./model";

/** Documentul cum îl are vizualizatorul: nordul sus, originea locală. */
export function inCadruLocal(brut: CanonicalDoc): CanonicalDoc {
  const doc = northUp(brut);
  return laOrigineLocala(doc, origineDesen(doc));
}

type Obiect = NonNullable<CanonicalDoc["objects"]>[number];
const props = (o: Obiect, cheie: string) =>
  ((o as { props?: Record<string, unknown> }).props?.[cheie] ?? {}) as Record<string, unknown>;
const nr = (x: unknown, alt = 0) => (typeof x === "number" && Number.isFinite(x) ? x : alt);

/** Cât de grav e un verdict: regula ia verdictul celui mai rău rând al ei. */
const GRAVITATE: Record<Verdict, number> = { pass: 0, warn: 1, fail: 2 };

export function modelDinDocument(brut: CanonicalDoc): ModelInstalatii {
  const doc = inCadruLocal(brut);
  const segmente: Segment[] = [];
  const echipamente: Echipament[] = [];
  const piese: PiesaSanitara[] = [];
  const aparate: Aparat[] = [];
  const apartamente: Apartament[] = [];
  for (const o of doc.objects ?? []) {
    const g = (o.geometry ?? {}) as Record<string, unknown>;
    const p = (o.properties ?? {}) as Record<string, unknown>;
    if (o.type === "MepSegment") {
      const s = props(o, "mep.segment");
      const axa = (g.axis ?? []) as number[][];
      const z = (s.z ?? []) as number[];
      const sectiune = s.sectiune as number[] | undefined;
      segmente.push({
        id: o.id, sistem: s.sistem as Sistem, rol: s.rol as Rol, storey: (p.storey as string) ?? null,
        building: (p.building as string) ?? null,
        puncte: axa.map((q, i): Punct3 => [q[0], q[1], nr(z[i], nr(z[z.length - 1]))]),
        dn: nr(s.dn), d_m: nr(s.d_m, 0.02), material: s.material as Material, mediu: s.mediu as Mediu,
        flat: (p.flat as string) ?? null,
        ...(typeof s.panta === "number" ? { panta: s.panta } : {}),
        ...(sectiune ? { sectiune: { l: sectiune[0], h: sectiune[1] } } : {}),
        ...(Array.isArray(s.directie) ? { directie: [nr((s.directie as number[])[0]), nr((s.directie as number[])[1])] } : {}),
        ...(Array.isArray(s.piese) ? { piese: s.piese as string[] } : {}),
        ...(typeof s.spre === "string" ? { spre: s.spre } : {}),
        ...(typeof s.ghena === "string" ? { ghena: s.ghena } : {}),
        ...(Array.isArray(s.fitinguri) ? { fitinguri: s.fitinguri as Fiting[] } : {}),
        ...(typeof s.circuit === "string" ? { circuit: s.circuit } : {}),
        ...(typeof s.tip_circuit === "string" ? { tip_circuit: s.tip_circuit as TipCircuit } : {}),
        ...(typeof s.cablu === "string" ? { cablu: s.cablu } : {}),
        ...(typeof s.fire === "number" ? { fire: s.fire } : {}),
        ...(typeof s.tub_mm === "number" ? { tub_mm: s.tub_mm } : {}),
        ...(Array.isArray(s.terminale) ? { terminale: s.terminale as string[] } : {}),
        ...(typeof s.articol === "string" ? { articol: s.articol, bucati: nr(s.bucati), imbinari: nr(s.imbinari) } : {}),
      });
    } else if (o.type === "MepEquipment") {
      const e = props(o, "mep.echipament");
      echipamente.push({
        id: o.id, sistem: e.sistem as Sistem, fel: e.fel as FelEchipament, storey: (p.storey as string) ?? null,
        building: (p.building as string) ?? null,
        centru: [nr(g.cx), nr(g.cy), nr(e.z_m)], dim: [nr(g.w), nr(g.h), nr(e.h_m)], deg: nr(g.deg),
        flat: (p.flat as string) ?? null,
        ...(Array.isArray(e.circuite) ? { circuite: e.circuite as Circuit[] } : {}),
        ...(typeof e.articol === "string" ? { articol: e.articol } : {}),
      });
    } else if (o.type === "Flat") {
      const poly = (g.poly ?? []) as number[][];
      if (poly.length >= 3) {
        apartamente.push({ id: o.id, storey: (p.storey as string) ?? null, building: (p.building as string) ?? null,
                           poly: poly.map((q): [number, number] => [q[0], q[1]]), group: (p.group as string) ?? null });
      }
    } else if (o.type === "MepTerminal") {
      // aparatele modulelor: capetele firelor, ca panoul să le spună pe nume;
      // o doză din editor ține mai multe aparate (`devices`), fiecare cu id-ul
      // `<doza>:<k>` (primul păstrează id-ul dozei), ca în motor (`_din_doza`)
      const mt = props(o, "mep.terminal");
      const devices = Array.isArray(mt.devices) ? (mt.devices as string[]) : [];
      if (mt.kind === "doza" && devices.length) {
        devices.forEach((kind, k) => aparate.push({ id: k ? `${o.id}:${k}` : o.id, kind,
                                                    storey: (p.storey as string) ?? null, flat: (p.flat as string) ?? null }));
      } else {
        aparate.push({ id: o.id, kind: String(mt.kind ?? ""),
                       storey: (p.storey as string) ?? null, flat: (p.flat as string) ?? null });
      }
    } else if (o.type === "Furniture") {
      // piesele sanitare ale arhitecturii: capetele la care ajung racordurile
      const kind = String(props(o, "architecture.piesa").kind ?? "");
      if (!(kind in PIESE_SANITARE)) continue;
      piese.push({
        id: o.id, kind, storey: (p.storey as string) ?? null, building: (p.building as string) ?? null,
        flat: (p.flat as string) ?? null, centru: [nr(g.cx), nr(g.cy)], dim: [nr(g.w), nr(g.h)], deg: nr(g.deg),
      });
    }
  }
  // intrările (ce a folosit motorul) vin din rezumatul soluției, nu din document
  return { segmente, echipamente, piese, aparate, apartamente, verificari: verificari(doc), intrari: [] };
}

/** Regulile `INS.*` ale documentului: un rând pe element vinovat (sau unul
 *  singur, `pass`) → o verificare pe regulă, cu elementele ei. */
function verificari(doc: CanonicalDoc): Verificare[] {
  const reguli = ((doc.compliance as { rules?: Record<string, unknown>[] } | null)?.rules ?? [])
    .filter((r) => String(r.id ?? "").startsWith("INS."));
  const pe = new Map<string, Verificare>();
  for (const r of reguli) {
    const id = String(r.id);
    const verdict = (["pass", "warn", "fail"].includes(r.status as string) ? r.status : "warn") as Verdict;
    const v = pe.get(id) ?? { regula: id, verdict: "pass" as Verdict, mesaj: r.message as Cheie,
                              sursa: String(r.citation ?? ""), elemente: [] };
    if (GRAVITATE[verdict] > GRAVITATE[v.verdict]) v.verdict = verdict;
    if (typeof r.subject === "string") v.elemente.push(r.subject);
    // mesajul regulii: cel al verdictului ei (`.ok` la trecute, `.pica` la vinovate)
    if (verdict !== "pass") v.mesaj = r.message as Cheie;
    pe.set(id, v);
  }
  return [...pe.values()]
    .map((v) => ({ ...v, param: { n: v.elemente.length } }))
    .sort((a, b) => GRAVITATE[b.verdict] - GRAVITATE[a.verdict] || a.regula.localeCompare(b.regula));
}

/** Intrările din rezumatul soluției `mep` (`summary.statistici`). */
export function intrariDinRezumat(statistici: Record<string, unknown> | null | undefined,
                                  scrise: readonly Intrare[] = []): Intrare[] {
  if (!statistici) return [...scrise];
  const n = (k: string) => nr(statistici[k]);
  return [
    { cheie: "inst.intrare.niveluri", valoare: "inst.val.niveluri",
      param: { n: n("niveluri"), calculate: n("tipuri_calculate"), copiate: n("niveluri_copiate") },
      sursa: "document" },
    { cheie: "inst.intrare.ghene", valoare: "inst.val.ghene",
      param: { n: n("ghene"), propuse: n("ghene_propuse") }, sursa: "sugerat" },
    { cheie: "inst.intrare.aparate", valoare: "inst.val.bucati", param: { n: n("terminale") },
      sursa: "document" },
    { cheie: "inst.intrare.durata", valoare: "inst.val.secunde", param: { s: n("durata_s") },
      sursa: "document" },
    // ce a scris motorul despre racordurile la rețele (cota canalului
    // stradal, de confirmat): `summary.intrari`
    ...scrise,
  ];
}
